import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import {
  User,
  Shield,
  Bell,
  Palette,
  HelpCircle,
  LogOut,
  ChevronRight,
  Moon,
  Sun,
  Globe,
  Smartphone,
  Mail,
  Lock,
  Linkedin,
  CreditCard,
  IndianRupee,
  Gift,
  Zap,
  UserCircle,
  FileText,
  Receipt
} from 'lucide-react';
import AccountSettings from '../components/settings/AccountSettings';
import { useUser, useClerk, useAuth } from '@clerk/clerk-react';
import AppLayout from '../components/AppLayout';
import { exportUserData } from '../lib/exportData';
import { LegalModal } from '../components/LegalModal';
import { FeedbackModal } from '../components/FeedbackModal';

const SettingsPage = () => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { getToken } = useAuth();
  const [activeSection, setActiveSection] = useState('general');
  const [legalModal, setLegalModal] = useState<{ open: boolean, type: 'terms' | 'privacy' | 'refund' }>({ open: false, type: 'terms' });
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [location] = useLocation();

  const handleSignOut = () => {
    signOut();
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSectionClick = (section: string) => {
    setActiveSection(section);
    // Add a small delay to ensure content is rendered before scrollings
    setTimeout(() => {
      if (section === 'privacy') {
        scrollToSection('privacy-security-section');
      } else if (section === 'support') {
        scrollToSection('account-section');
      }
    }, 100);
  };

  const settingSections = [
    {
      title: 'Account',
      items: [
        {
          icon: User,
          label: 'Your Profile',
          value: 'Manage your name, password, and account settings',
          action: () => setActiveSection('profile'),
          showChevron: true,
          showToggle: false,
          toggleValue: false
        },
        {
          icon: Mail,
          label: 'Contact Support',
          value: 'Get help and support',
          action: () => setIsFeedbackModalOpen(true),
          showChevron: true,
          showToggle: false,
          toggleValue: false
        },
        {
          icon: CreditCard,
          label: 'Subscription & Billing',
          value: 'Manage your plan',
          action: () => window.location.href = '/payments',
          showChevron: true,
          showToggle: false,
          toggleValue: false
        },
        {
          icon: Gift,
          label: 'Gift a Subscription',
          value: 'Give someone the gift of growth',
          action: () => window.location.href = '/gift',
          showChevron: true,
          showToggle: false,
          toggleValue: false
        }
      ]
    },
    {
      title: 'Privacy & Security',
      items: [
        {
          icon: Shield,
          label: 'Privacy Policy',
          value: 'View our privacy practices',
          action: () => setLegalModal({ open: true, type: 'privacy' }),
          showChevron: true,
          showToggle: false,
          toggleValue: false
        },
        {
          icon: FileText,
          label: 'Terms of Use',
          value: 'Review the terms of use',
          action: () => setLegalModal({ open: true, type: 'terms' }),
          showChevron: true,
          showToggle: false,
          toggleValue: false
        },
        {
          icon: Receipt,
          label: 'Refund Policy',
          value: 'Review the refund policy',
          action: () => setLegalModal({ open: true, type: 'refund' }),
          showChevron: true,
          showToggle: false,
          toggleValue: false
        },
        {
          icon: Lock,
          label: 'Data Export',
          value: 'Download your data',
          action: async () => {
            const token = await getToken();
            if (token) {
              exportUserData(token);
            }
          },
          showChevron: true,
          showToggle: false,
          toggleValue: false
        }
      ]
    },
  ];

  // Pars the URL hash for deep linking
  useEffect(() => {
    if (location.includes('#')) {
      const hash = location.split('#')[1];
      if (['general', 'profile', 'privacy', 'support'].includes(hash)) {
        setActiveSection(hash);
      }
    }
  }, [location]);

  // Scroll to top when switching to profile section
  useEffect(() => {
    if (activeSection === 'profile') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeSection]);

  return (
    <AppLayout currentPage="settings">
      <LegalModal open={legalModal.open} type={legalModal.type} onOpenChange={(open) => setLegalModal({ ...legalModal, open })} />
      <FeedbackModal open={isFeedbackModalOpen} onOpenChange={setIsFeedbackModalOpen} />
      <div className="min-h-screen px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-8">

          {/* Page Header */}
          <div className="text-center space-y-4 px-2">
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
              My Account
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Manage your account, preferences, and privacy settings.
            </p>

            {/* Section Tabs */}
            <div className="inline-flex flex-wrap sm:flex-nowrap p-1.5 bg-slate-100/50 rounded-2xl gap-1.5 max-w-full overflow-x-auto scrollbar-none">
              <button
                onClick={() => setActiveSection('general')}
                className={`flex-1 sm:flex-none whitespace-nowrap px-6 py-2.5 rounded-xl font-display text-sm font-semibold transition-all duration-200 ${activeSection === 'general'
                  ? 'bg-white text-royal-blue shadow-sm ring-1 ring-black/5'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                  }`}
              >
                General
              </button>
              <button
                onClick={() => setActiveSection('profile')}
                className={`flex-1 sm:flex-none whitespace-nowrap px-6 py-2.5 rounded-xl font-display text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${activeSection === 'profile'
                  ? 'bg-white text-royal-blue shadow-sm ring-1 ring-black/5'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                  }`}
              >
                <UserCircle className="h-4 w-4" strokeWidth={2} />
                Personal Profile
              </button>
              <button
                onClick={() => handleSectionClick('privacy')}
                className={`flex-1 sm:flex-none whitespace-nowrap px-6 py-2.5 rounded-xl font-display text-sm font-semibold transition-all duration-200 ${activeSection === 'privacy'
                  ? 'bg-white text-royal-blue shadow-sm ring-1 ring-black/5'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                  }`}
              >
                Privacy
              </button>
              <button
                onClick={() => handleSectionClick('support')}
                className={`flex-1 sm:flex-none whitespace-nowrap px-6 py-2.5 rounded-xl font-display text-sm font-semibold transition-all duration-200 ${activeSection === 'support'
                  ? 'bg-white text-royal-blue shadow-sm ring-1 ring-black/5'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                  }`}
              >
                Support
              </button>
            </div>
          </div>


          {/* Conditional Content Based on Active Section */}
          {activeSection === 'profile' ? (
            <AccountSettings />
          ) : (
            <div className="space-y-8">
              {settingSections.map((section) => {
                const sectionId = section.title === 'Account' ? 'account-section' : 
                                 section.title === 'Privacy & Security' ? 'privacy-security-section' : 
                                 section.title.toLowerCase().replace(/\s+/g, '-') + '-section';
                
                return (
                  <div key={section.title} id={sectionId} className="space-y-4">
                    <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider px-4">
                      {section.title}
                    </h2>

                  <div className="rounded-3xl bg-white/80 backdrop-blur-sm shadow-sm overflow-hidden">
                    {section.items.map((item, index) => (
                      <button
                        key={item.label}
                        onClick={item.action}
                        className={`w-full flex items-center justify-between p-6 text-left hover:bg-slate-50/80 transition-colors ${index !== section.items.length - 1 ? 'border-b border-slate-100' : ''
                          }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
                            <item.icon className="h-5 w-5" strokeWidth={1.5} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900">{item.label}</p>
                            <p className="text-sm text-slate-600 truncate">{item.value}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          {item.showToggle && (
                            <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${item.toggleValue ? 'bg-blue-600' : 'bg-slate-200'
                              }`}>
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.toggleValue ? 'translate-x-6' : 'translate-x-1'
                                }`} />
                            </div>
                          )}

                          {item.showChevron && (
                            <ChevronRight className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  </div>
                );
              })}

              {/* Community Section */}
              <div className="space-y-4">
                <h2 className="text-sm font-medium text-slate-500 uppercase tracking-wider px-2">Community</h2>
                <div className="rounded-3xl bg-white/80 backdrop-blur-sm shadow-sm overflow-hidden">
                  <a
                    href="https://www.linkedin.com/company/hipo-career-solutions-llp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-[#0077B5]/5 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#0077B5]/10 text-[#0077B5]">
                        <Linkedin className="h-5 w-5" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Connect with HiPo Community</p>
                        <p className="text-sm text-slate-500">Join our professional network</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
                  </a>
                </div>
              </div>

              {/* Sign Out Section */}
              <div className="space-y-4">
                <div className="rounded-3xl bg-white/80 backdrop-blur-sm shadow-sm overflow-hidden">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-red-50/80 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-red-100 text-red-600">
                        <LogOut className="h-5 w-5" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="font-medium text-red-600">Sign Out</p>
                        <p className="text-sm text-red-500">Sign out of your account</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-red-400" strokeWidth={1.5} />
                  </button>
                </div>
              </div>

            </div>
          )}

          {/* App Info */}
          <div className="text-center space-y-2 pt-8">
            <p className="text-sm text-slate-500">HiPo AI Coach</p>
            <p className="text-xs text-slate-400">Version 1.0.0 • Built with ❤️</p>
          </div>
        </div>
      </div>
    </AppLayout>);
};

export default SettingsPage;
